import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import { isValidBooleanValue, stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import styles from './ids-color-picker.scss';
import IdsColor from '../ids-color/ids-color';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsElement from '../../core/ids-element';
import IdsFieldHeightMixin from '../../mixins/ids-field-height-mixin/ids-field-height-mixin';
import IdsLabelStateParentMixin from '../../mixins/ids-label-state-mixin/ids-label-state-parent-mixin';
import { IdsPopupElementRef } from '../ids-popup/ids-popup-attributes';
import IdsClearableMixin from '../../mixins/ids-clearable-mixin/ids-clearable-mixin';
import IdsDirtyTrackerMixin from '../../mixins/ids-dirty-tracker-mixin/ids-dirty-tracker-mixin';
import IdsAutoSelectMixin from '../../mixins/ids-autoselect-mixin/ids-autoselect-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsPopup from '../ids-popup/ids-popup';
import IdsTriggerButton from '../ids-trigger-field/ids-trigger-button';
import '../ids-trigger-field/ids-trigger-field';
import type IdsTriggerField from '../ids-trigger-field/ids-trigger-field';
import { IdsLabelStateMode } from '../../mixins/ids-label-state-mixin/ids-label-state-common';
import { getClosest } from '../../utils/ids-dom-utils/ids-dom-utils';

// Optimize the mixin chain by creating a single combined base class
const Base = IdsAutoSelectMixin(
  IdsClearableMixin(
    IdsFieldHeightMixin(
      IdsDirtyTrackerMixin(
        IdsLocaleMixin(
          IdsLabelStateParentMixin(
            IdsEventsMixin(
              IdsElement
            )
          )
        )
      )
    )
  )
);

/**
 * IDS Color Picker
 * @type {IdsColorPicker}
 * @inherits IdsElement
 * @mixes IdsAutoSelectMixin
 * @mixes IdsClearableMixin
 * @mixes IdsFieldHeightMixin
 * @mixes IdsDirtyTrackerMixin
 * @mixes IdsLocaleMixin
 * @mixes IdsLabelStateParentMixin
 * @mixes IdsEventsMixin
 */
@customElement('ids-color-picker')
@scss(styles)
export default class IdsColorPicker extends Base {
  isFormComponent = true;

  useDefaultSwatches = false;

  initialized = false;

  // Static cache for default swatches to avoid recreating them
  private static defaultSwatchesCache: Array<IdsColor> | null = null;

  constructor() {
    super();
  }

  /** Invoked each time the custom element is added to the DOM */
  connectedCallback() {
    super.connectedCallback();

    // Set up essential functionality immediately
    if (this.clearable) this.appendClearableButton();
    this.#updateColor(this.value);

    // Defer expensive operations to improve initial render time
    requestAnimationFrame(() => {
      if (!this.swatches.length) {
        this.useDefaultSwatches = true;
        const fragment = document.createDocumentFragment();
        this.defaultSwatches.forEach((swatch) => fragment.appendChild(swatch));
        this.appendChild(fragment);
      }
      this.#configureSwatches();
      this.#attachEventHandlers();
      this.initialized = true;
    });
  }

  /** Invoked each time the custom element is removed from the DOM */
  disconnectedCallback(): void {
    // Clean up all resources
    this.#detachEventHandlers();

    // Clear any references that might cause memory leaks
    if (IdsColorPicker.defaultSwatchesCache && !document.querySelector('ids-color-picker')) {
      IdsColorPicker.defaultSwatchesCache = null;
    }

    super.disconnectedCallback();
  }

  /** @returns {string[]} this component's observable attributes */
  static get attributes(): string[] {
    return [
      ...super.attributes,
      attributes.ADVANCED,
      attributes.CHECKED,
      attributes.COMPACT,
      attributes.DISABLED,
      attributes.ID,
      attributes.LABEL,
      attributes.PLACEHOLDER,
      attributes.SUPPRESS_LABELS,
      attributes.READONLY,
      attributes.TABBABLE,
      attributes.TABINDEX,
      attributes.SIZE,
      attributes.SUPPRESS_TOOLTIPS,
      attributes.SUPPRESS_ERROR_MESSAGE,
      attributes.TOOLTIP,
      attributes.VALIDATE,
      attributes.VALUE,
    ];
  }

  /**
   * HTML for IdsColorPicker.shadowRoot
   * Contains default slot for color elements
   * @returns {string} - html for the template
   */
  template(): string {
    const swatchSize = this.swatchSize();
    const readonly = this.readonly ? 'readonly' : '';
    const disabled = this.disabled ? 'disabled' : '';
    const dirtyTracker = this.dirtyTracker ? 'dirty-tracker' : '';
    const advancedPopup = this.advanced ? this.colorPickerAdvancedHtml : '';
    const value = this.value ? `value="${this.value}"` : '';
    const label = this.label ? `label="${this.label}"` : '';

    return `<div class="ids-color-picker">
      ${advancedPopup}
      <ids-trigger-field ${label} ${readonly} ${disabled} ${dirtyTracker} ${value}>
        <ids-color ${disabled} size="${swatchSize}" tabindex="-1" class="color-preview" slot="trigger-start"></ids-color>
        <ids-trigger-button slot="trigger-end" class="color-picker-trigger-btn">
          <ids-text audible="true" translate-text="true"></ids-text>
          <ids-icon icon="dropdown"></ids-icon>
        </ids-trigger-button>
      </ids-trigger-field>
      <ids-popup id="color-picker-popup" type="menu" position-style="fixed">
        <slot slot="content" class="color-popup"></slot>
      </ids-popup>
    </ids-color-picker>`;
  }

  /**
   * Get the text input element
   * @returns {IdsTriggerField | null} the trigger field element
   */
  get input(): IdsTriggerField | null {
    return this.container?.querySelector<IdsTriggerField>('ids-trigger-field') || null;
  }

  /**
   * Get the color input element for advanced mode
   * @returns {HTMLInputElement | null} the color input element
   */
  get colorInput(): HTMLInputElement | null {
    return this.container?.querySelector<HTMLInputElement>('.advanced-color-picker') || null;
  }

  /**
   * Get the color preview element
   * @returns {HTMLElement | null} the color preview element
   */
  get colorPreview(): HTMLElement | null {
    return this.container?.querySelector<HTMLElement>('.color-preview') || null;
  }

  /**
   * Get the trigger button element
   * @returns {IdsTriggerButton | null} the trigger button element
   */
  get triggerBtn(): IdsTriggerButton | null {
    return this.container?.querySelector<IdsTriggerButton>('ids-trigger-button') || null;
  }

  /**
   * HTML for Color Picker Previw Swatch
   * @returns {string} - html
   */
  get colorPreviewHtml(): string {
    return `<ids-color${this.disabled ? ' disabled' : ''} tabindex="-1" class="color-preview" size="${this.swatchSize()}"></ids-color>`;
  }

  /**
   * Get the popup element
   * @returns {IdsPopup | null} the popup element
   */
  get popup() {
    return this.container?.querySelector<IdsPopup>('#color-picker-popup');
  }

  /**
   * HTML for Advanced Color Picker Popup
   * @returns {string} - html
   */
  get colorPickerAdvancedHtml(): string {
    if (!this.advanced) return '';
    return `
    <label id="label" for="advanced-color-picker"></label>
    <input id="advanced-color-picker" class="advanced-color-picker" tabindex="-1" type="color"/>`;
  }

  /**
   * Available color swatches within this color-picker
   * @returns {IdsColor[]} available colors within this picker
   */
  get swatches(): Array<IdsColor> {
    return [...this.querySelectorAll<IdsColor>('ids-color')];
  }

  /**
   * Default color swatches for this color-picker if no children provided
   * @returns {Array<IdsColor>} available colors within this picker
   */
  get defaultSwatches(): Array<IdsColor> {
    // Use cached swatches if available to avoid expensive recreation
    if (IdsColorPicker.defaultSwatchesCache) {
      return IdsColorPicker.defaultSwatchesCache.map((swatch) => swatch.cloneNode(true) as IdsColor);
    }

    // Create swatches only once
    const swatches = this.#createDefaultSwatches();
    IdsColorPicker.defaultSwatchesCache = swatches.map((swatch) => swatch.cloneNode(true) as IdsColor);
    return swatches;
  }

  /**
   * Creates the default color swatches
   * @private
   * @returns {Array<IdsColor>} array of color swatches
   */
  #createDefaultSwatches(): Array<IdsColor> {
    const COLOR_PALETTE_CSS_VAR_REGEX = /^--ids-color-(([^0-9]+)-([0-9]+))$/;
    const swatches: Array<IdsColor> = [];

    const createColor = ((cssVar: string) => {
      const color = new IdsColor();
      const [cssVarName, label, colorCategory, colorCode] = cssVar.match(COLOR_PALETTE_CSS_VAR_REGEX) || [];
      color.label = label;
      color.tooltip = `${colorCategory} ${colorCode}`;
      color.hex = getComputedStyle(this.parentElement as HTMLElement).getPropertyValue(String(cssVarName)) || `var(${cssVarName})`;
      color.classList.add((Number(colorCode) < 40) ? 'light' : 'dark');
      return color;
    });

    // Define color groups once to avoid recreating arrays
    const colorGroups = [
      ['--ids-color-red-10', '--ids-color-red-20', '--ids-color-red-30', '--ids-color-red-40', '--ids-color-red-50', '--ids-color-red-60', '--ids-color-red-70', '--ids-color-red-80', '--ids-color-red-90', '--ids-color-red-100'],
      ['--ids-color-orange-10', '--ids-color-orange-20', '--ids-color-orange-30', '--ids-color-orange-40', '--ids-color-orange-50', '--ids-color-orange-60', '--ids-color-orange-70', '--ids-color-orange-80', '--ids-color-orange-90', '--ids-color-orange-100'],
      ['--ids-color-green-10', '--ids-color-green-20', '--ids-color-green-30', '--ids-color-green-40', '--ids-color-green-50', '--ids-color-green-60', '--ids-color-green-70', '--ids-color-green-80', '--ids-color-green-90', '--ids-color-green-100'],
      ['--ids-color-blue-10', '--ids-color-blue-20', '--ids-color-blue-30', '--ids-color-blue-40', '--ids-color-blue-50', '--ids-color-blue-60', '--ids-color-blue-70', '--ids-color-blue-80', '--ids-color-blue-90', '--ids-color-blue-100'],
      ['--ids-color-teal-10', '--ids-color-teal-20', '--ids-color-teal-30', '--ids-color-teal-40', '--ids-color-teal-50', '--ids-color-teal-60', '--ids-color-teal-70', '--ids-color-teal-80', '--ids-color-teal-90', '--ids-color-teal-100'],
      ['--ids-color-purple-10', '--ids-color-purple-20', '--ids-color-purple-30', '--ids-color-purple-40', '--ids-color-purple-50', '--ids-color-purple-60', '--ids-color-purple-70', '--ids-color-purple-80', '--ids-color-purple-90', '--ids-color-purple-100'],
      ['--ids-color-neutral-10', '--ids-color-neutral-20', '--ids-color-neutral-30', '--ids-color-neutral-40', '--ids-color-neutral-50', '--ids-color-neutral-60', '--ids-color-neutral-70', '--ids-color-neutral-80', '--ids-color-neutral-90', '--ids-color-neutral-100'],
    ];

    // Create all swatches at once
    colorGroups.forEach((group) => {
      group.forEach((cssVar) => {
        const color = createColor(cssVar);
        swatches.push(color);
      });
    });

    return swatches;
  }

  /**
   * Sets the value attribute
   * @param {string} value - string value from the value attribute
   */
  set value(value: string | null) {
    value = value?.trim() ?? '';

    if (value) {
      this.setAttribute(attributes.VALUE, value);
    } else {
      this.removeAttribute(attributes.VALUE);
    }

    this.#updateColorPickerValues(value);
    this.#updateSelectedSwatch(this.#findColorSwatch(value));
  }

  /**
   * Gets the value attribute
   * @returns {string} - string value from the value attribute
   */
  get value(): string {
    return this.getAttribute(attributes.VALUE) ?? '';
  }

  /**
   * Set the placeholder text
   * @param {string | null} value the placeholder text
   */
  set placeholder(value: string | null) {
    if (value) {
      this.setAttribute(attributes.PLACEHOLDER, value);
      this.input?.setAttribute(attributes.PLACEHOLDER, value);
    } else {
      this.removeAttribute(attributes.PLACEHOLDER);
      this.input?.removeAttribute(attributes.PLACEHOLDER);
    }
  }

  /**
   * Get the placeholder text
   * @returns {string} the placeholder text
   */
  get placeholder(): string {
    return this.getAttribute(attributes.PLACEHOLDER) ?? '';
  }

  /**
   * Set the size of the color picker
   * @param {string | null} val the size value
   */
  set size(val: string | null) {
    if (val) {
      this.setAttribute(attributes.SIZE, val);
      this.input?.setAttribute(attributes.SIZE, val);
    } else {
      this.removeAttribute(attributes.SIZE);
      this.input?.removeAttribute(attributes.SIZE);
    }
  }

  /**
   * Get the size of the color picker
   * @returns {string} the size value
   */
  get size(): string {
    return this.getAttribute(attributes.SIZE) || 'md';
  }

  /**
   * Set the validation rules
   * @param {string | null} val the validation rules
   */
  set validate(val: string | null) {
    if (val) {
      this.setAttribute(attributes.VALIDATE, val);
      this.input?.setAttribute(attributes.VALIDATE, val);
    } else {
      this.removeAttribute(attributes.VALIDATE);
      this.input?.removeAttribute(attributes.VALIDATE);
    }
  }

  /**
   * Get the validation rules
   * @returns {string | null} the validation rules
   */
  get validate(): string | null {
    return this.getAttribute(attributes.VALIDATE);
  }

  /**
   * Set the validation rules
   * @param {string | null} val the validation rules
   */
  set suppressValidation(val: boolean | null) {
    const boolVal = isValidBooleanValue(val) && stringToBool(val);

    this.toggleAttribute(attributes.SUPPRESS_VALIDATION, boolVal);
    this.input?.toggleAttribute(attributes.SUPPRESS_VALIDATION, boolVal);
  }

  /**
   * Get the suppress validation state
   * @returns {boolean | null} the validation rules
   */
  get suppressValidation(): boolean | null {
    return stringToBool(this.getAttribute(attributes.SUPPRESS_VALIDATION));
  }

  /**
   * Sets whether validation is suppressed on the inner input element.
   * @param {boolean | string} val true to suppress validation
   */
  set suppressErrorMessage(val: boolean | string) {
    const boolVal = isValidBooleanValue(val) && stringToBool(val);

    this.toggleAttribute(attributes.SUPPRESS_ERROR_MESSAGE, boolVal);
    this.input?.toggleAttribute(attributes.SUPPRESS_ERROR_MESSAGE, boolVal);
  }

  /**
   * Gets whether validation is suppressed on the inner input element.
   * @returns {boolean} true if validation is suppressed
   */
  get suppressErrorMessage(): boolean {
    return this.hasAttribute(attributes.SUPPRESS_ERROR_MESSAGE);
  }

  /**
   * Set the tabbable state
   * @param {boolean | string} value whether the element is tabbable
   */
  set tabbable(value: boolean | string) {
    const isTabbable = stringToBool(value);
    if (isTabbable) {
      this.setAttribute(attributes.TABBABLE, '');
      this.input?.setAttribute(attributes.TABBABLE, '');
      this.triggerBtn?.setAttribute(attributes.TABBABLE, '');
    } else {
      this.removeAttribute(attributes.TABBABLE);
      this.input?.removeAttribute(attributes.TABBABLE);
      this.triggerBtn?.removeAttribute(attributes.TABBABLE);
    }
  }

  /**
   * Get the tabbable state
   * @returns {boolean | string} whether the element is tabbable
   */
  get tabbable(): boolean | string {
    return stringToBool(this.getAttribute(attributes.TABBABLE));
  }

  /**
   * Set the tooltip text
   * @param {string | null} value the tooltip text
   */
  set tooltip(value: string | null) {
    if (value) {
      this.setAttribute(attributes.TOOLTIP, value);
      this.input?.setAttribute(attributes.TOOLTIP, value);
    } else {
      this.removeAttribute(attributes.TOOLTIP);
      this.input?.removeAttribute(attributes.TOOLTIP);
    }
  }

  /**
   * Get the tooltip text
   * @returns {string | null} the tooltip text
   */
  get tooltip(): string | null {
    return this.getAttribute(attributes.TOOLTIP);
  }

  /**
   * Sets the advanced attribute
   * @param {boolean | string} value - true if the "advanced" color picker type should be used
   */
  set advanced(value: boolean | string) {
    const pattern = ['#', /[0-9a-fA-F]/, /[0-9a-fA-F]/, /[0-9a-fA-F]/, /[0-9a-fA-F]/, /[0-9a-fA-F]/, /[0-9a-fA-F]/];

    if (stringToBool(value)) {
      this.setAttribute(attributes.ADVANCED, 'true');
      if (!this.colorInput) this.container?.insertAdjacentHTML('afterbegin', this.colorPickerAdvancedHtml);
      this.#attachColorInputEventHandlers();
      this.#updateColorPickerValues(this.value);
      if (this.input) this.input.mask = pattern;
    } else {
      this.removeAttribute(attributes.ADVANCED);
      this.#detachColorInputEventHandlers();
      this.colorInput?.remove();
      if (this.input) this.input.mask = undefined;
    }
  }

  /**
   * Gets the advanced attribute
   * @returns {boolean | string} - true if the "advanced" color picker type should be used
   */
  get advanced(): boolean | string {
    return stringToBool(this.getAttribute(attributes.ADVANCED)) || false;
  }

  /**
   * Sets the disabled attribute
   * @param {boolean | string} value - string value from the disabled attribute
   */
  set disabled(value: boolean | string) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.DISABLED, '');
      this.input?.setAttribute(attributes.DISABLED, '');
      this.triggerBtn?.setAttribute(attributes.DISABLED, '');
      this.triggerBtn?.setAttribute(attributes.TABBABLE, 'false');
      if (this.hasAttribute(attributes.READONLY)) {
        this.removeAttribute(attributes.READONLY);
        this.input?.removeAttribute(attributes.READONLY);
      }
    } else {
      this.removeAttribute(attributes.DISABLED);
      this.input?.removeAttribute(attributes.DISABLED);
      this.triggerBtn?.removeAttribute(attributes.DISABLED);
      this.triggerBtn?.setAttribute(attributes.TABBABLE, String(this.tabbable));
    }
  }

  /**
   * Gets the disabled attribute
   * @returns {boolean | string} - string value from the disabled attribute
   */
  get disabled(): boolean | string {
    return stringToBool(this.getAttribute(attributes.DISABLED));
  }

  /**
   * Sets the readonly attribute
   * @param {boolean | string} value - string value from the readonly attribute
   */
  set readonly(value: boolean | string) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.READONLY, '');
      this.input?.setAttribute(attributes.READONLY, '');
      this.triggerBtn?.setAttribute(attributes.DISABLED, '');
      if (this.hasAttribute(attributes.DISABLED)) {
        this.removeAttribute(attributes.DISABLED);
        this.input?.removeAttribute(attributes.DISABLED);
        this.triggerBtn?.setAttribute(attributes.TABBABLE, String(this.tabbable));
      }
    } else {
      this.removeAttribute(attributes.READONLY);
      this.input?.removeAttribute(attributes.READONLY);
      this.triggerBtn?.removeAttribute(attributes.DISABLED);
    }
  }

  /**
   * Gets the readonly attribute
   * @returns {boolean | string} - value string from the readonly attribute
   */
  get readonly(): boolean | string {
    return stringToBool(this.getAttribute(attributes.READONLY));
  }

  /**
   * Show color-hex values instead of labels
   * @param {boolean | string} value - true if color-swatch labels should show instead of hexes
   */
  set suppressLabels(value: boolean | string) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.SUPPRESS_LABELS, 'true');
    } else {
      this.removeAttribute(attributes.SUPPRESS_LABELS);
    }

    this.#configureSwatches();
  }

  /**
   * Gets the labels attribute
   * @returns {boolean | string} - true if color-swatch labels should show instead of hexes
   */
  get suppressLabels(): boolean {
    if (this.advanced) return true;

    return stringToBool(this.getAttribute(attributes.SUPPRESS_LABELS)) || false;
  }

  /**
   * Sets the tooltips attribute
   * @param {boolean | string} value - true if color-swatch tooltips should show
   */
  set suppressTooltips(value: boolean | string) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.SUPPRESS_TOOLTIPS, 'true');
    } else {
      this.removeAttribute(attributes.SUPPRESS_TOOLTIPS);
    }

    this.#configureSwatches();
  }

  /**
   * Get the suppress tooltips state
   * @returns {boolean | string} true if color-swatch tooltips should show
   */
  get suppressTooltips(): boolean | string {
    if (this.advanced) return true;

    return stringToBool(this.getAttribute(attributes.SUPPRESS_TOOLTIPS)) || false;
  }

  /**
   * Set the compact mode
   * @param {boolean | string} value whether to use compact mode
   */
  set compact(value: boolean | string) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.COMPACT, '');
      this.input?.setAttribute(attributes.COMPACT, '');
      this.input?.querySelector('ids-color')?.setAttribute(attributes.SIZE, 'mm');
    } else {
      this.removeAttribute(attributes.COMPACT);
      this.input?.removeAttribute(attributes.COMPACT);
      this.input?.querySelector('ids-color')?.setAttribute(attributes.SIZE, 'md');
    }
  }

  /**
   * Get the compact mode state
   * @returns {boolean | string} whether compact mode is enabled
   */
  get compact(): boolean | string {
    return stringToBool(this.getAttribute(attributes.COMPACT));
  }

  /**
   * Set the id internally and externally
   * @param {string} value id value
   */
  set id(value: string) {
    this.shadowRoot?.querySelector('ids-trigger-field')?.setAttribute(attributes.ID, `${value}-trigger-field`);
    this.setAttribute(attributes.ID, value);
  }

  /**
   * Get the id value
   * @returns {string} the id value
   */
  get id(): string {
    return this.getAttribute(attributes.ID) || 'none';
  }

  /**
   * Closes the Color Picker's Popup
   * @returns {void}
   */
  close(): void {
    this.popup?.setAttribute(attributes.VISIBLE, 'false');
    this.popup?.removeOpenEvents();
    this.#configureSwatches();
  }

  /**
   * Opens the Color Picker's Popup
   * @returns {void}
   */
  open(): void {
    if (this.advanced || this.disabled || this.readonly || !this.popup) {
      return;
    }

    // Refresh Swatches using DocumentFragment for better performance
    if (this.useDefaultSwatches) {
      const fragment = document.createDocumentFragment();
      // Remove existing swatches
      this.querySelectorAll('ids-color').forEach((el) => el.remove());
      // Add new swatches to fragment first, then append once to DOM
      this.defaultSwatches.forEach((swatch) => fragment.appendChild(swatch));
      this.appendChild(fragment);
    }

    const rtl = this.localeAPI.isRTL();

    let yAlignment = '';

    if (rtl && ['xs', 'sm', 'mm'].includes(this.size)) {
      yAlignment = 'right';
    } else if (!rtl && ['md', 'lg', 'full'].includes(this.size)) {
      yAlignment = 'right';
    } else {
      yAlignment = 'left';
    }

    // Cache DOM queries to avoid repeated lookups
    const triggerBtn = this.triggerBtn;
    const textInput = this.input;

    const isWithinModal = !!getClosest(this, 'ids-modal');
    this.popup.offsetContainer = isWithinModal ? 'ids-modal' : '';
    this.popup.align = `bottom, ${yAlignment}`;
    this.popup.arrow = 'bottom';

    if (triggerBtn && textInput) {
      this.popup.arrowTarget = triggerBtn.shadowRoot?.querySelector('button') as IdsPopupElementRef;
      this.popup.alignTarget = (textInput.shadowRoot?.querySelector('.ids-input')?.querySelector('.field-container') as IdsPopupElementRef);
    }
    this.#updateColumnCount();

    this.popup.y = 0;
    this.popup.visible = true;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.popup.show();
    this.popup?.addOpenEvents();
    this.#configureSwatches(true);
  }

  /** Even out the columns */
  #updateColumnCount() {
    const allItems = this.querySelectorAll('ids-color');
    const rowStarts = this.querySelectorAll('ids-color[row-start]');

    let columns = 10; // Default
    if (rowStarts.length > 0) {
      const rows = rowStarts.length + 1;
      columns = Math.ceil(allItems.length / rows);
    }

    // Set the CSS variable
    this.style.setProperty('--ids-color-picker-grid-columns', columns.toString());
  }

  /**
   * Inherited from the Popup Open Events Mixin.
   * Runs when a click event is propagated to the window.
   * @returns {void}
   */
  onOutsideClick(): void {
    this.close();
  }

  /** Handle events */
  #attachEventHandlers(): void {
    this.#detachEventHandlers();

    // Use event delegation for better performance
    // Handle all click events in a single handler
    this.onEvent('click.color-picker-container', this.container, (evt: MouseEvent) => {
      const target = evt.target as HTMLElement;
      const isEditable = !this.readonly && !this.disabled;
      if (!isEditable) return;

      // Check if click is on trigger button or color preview
      if (target.closest('.color-picker-trigger-btn, .color-preview')) {
        this.#openCloseColorpicker();
        return;
      }

      // Check if click is on a color swatch
      const colorSwatch = target.closest('ids-color');
      if (colorSwatch && !colorSwatch.classList.contains('color-preview')) {
        this.#updateColor((colorSwatch as any).hex);
        this.close();
      }
    });

    // Handle keyboard on trigger button
    const triggerBtn = this.triggerBtn;
    if (triggerBtn) {
      this.onEvent('keydown.trigger-btn', triggerBtn, (evt: KeyboardEvent) => {
        if (evt.key === 'Enter' || evt.key === ' ' || evt.key === 'Space') {
          evt.preventDefault();
          evt.stopPropagation();

          const isEditable = !this.readonly && !this.disabled;
          if (isEditable) {
            if (this.popup?.visible) {
              this.close();
            } else {
              this.open();
            }
          }
        }
      });
    }

    // Handle IdsTriggerField change events with a single handler
    const textInput = this.input;
    if (textInput) {
      this.onEvent('change', textInput, (evt: CustomEvent) => {
        evt.stopPropagation();
        this.#updateColor(evt.detail.value);

        // Propagate ids trigger field change event
        this.triggerEvent('change', this, {
          bubbles: true,
          detail: {
            ...evt.detail,
            elem: this
          }
        });
      });

      // Handle input events
      this.onEvent('input', textInput, (evt: CustomEvent) => {
        evt.stopPropagation();
        const target = evt.target as HTMLInputElement;
        this.#updateColorPreview(target.value);

        // Propagate ids trigger field input event
        this.triggerEvent('input', this, {
          bubbles: true,
          detail: {
            elem: this,
            value: target?.value
          }
        });
      });
    }

    // If enabled, attach advanced color picker handlers
    if (this.advanced) {
      this.#attachColorInputEventHandlers();
    }

    // Keyboard navigation - use a single handler for all keyboard events
    this.onEvent('keydown.color-picker', this, (e: KeyboardEvent) => {
      const key = e.key;

      // Handle escape key
      if (key === 'Escape') {
        this.close();
        return;
      }

      // Handle enter or space
      if (['Enter', 'Space', ' '].includes(key)) {
        if (this.popup?.visible) {
          const target = e.target as HTMLElement;
          const isColorSwatch = target.tagName.toLowerCase() === 'ids-color';
          const swatch = isColorSwatch ? target : document.activeElement as HTMLElement;
          this.#updateColor((swatch as any)?.hex);
          this.close();
        } else if (key === 'Enter') {
          this.open();
        }
        return;
      }

      // Handle arrow down to open popup
      if (key === 'ArrowDown' && !this.popup?.visible) {
        e.preventDefault();
        this.open();
      }
    });

    // Optimize popup keyboard navigation with a single handler
    const popup = this.popup;
    if (popup) {
      this.onEvent('keydown.color-picker-popup', popup, this.#handlePopupKeyNavigation);
    }
  }

  /**
   * Handle keyboard navigation within the popup
   * @private
   * @param {KeyboardEvent} e - The keyboard event
   */
  #handlePopupKeyNavigation = (e: KeyboardEvent): void => {
    const key = e.key;
    const navigationKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space', ' '];

    if (!navigationKeys.includes(key)) return;

    e.preventDefault();

    // Constants for navigation
    const NUM_COLUMNS = 10;
    const swatches = this.swatches;
    if (!swatches.length) return;

    const firstSwatch = swatches[0];
    const lastSwatch = swatches[swatches.length - 1];

    // Find current swatch
    const target = e.target as HTMLElement;
    const activeSwatch = document.activeElement?.closest('ids-color') || firstSwatch;
    const currentSwatch = (target.tagName.toLowerCase() === 'ids-color') ? target : activeSwatch;
    const currentSwatchIndex = swatches.indexOf(currentSwatch as IdsColor);

    // Handle different arrow keys
    switch (key) {
      case 'ArrowRight': {
        const nextSwatch = swatches[currentSwatchIndex + 1] || firstSwatch;
        nextSwatch.swatch?.focus();
        break;
      }

      case 'ArrowLeft': {
        const previousSwatch = swatches[currentSwatchIndex - 1] || lastSwatch;
        previousSwatch.swatch?.focus();
        break;
      }

      case 'ArrowUp': {
        const upwardIndex = currentSwatchIndex - NUM_COLUMNS;
        if (upwardIndex >= 0) {
          swatches[upwardIndex]?.swatch?.focus();
        }
        break;
      }

      case 'ArrowDown': {
        const downwardIndex = currentSwatchIndex + NUM_COLUMNS;
        if (downwardIndex < swatches.length) {
          swatches[downwardIndex]?.swatch?.focus();
        }
        break;
      }

      default:
        break;
    }
  };

  /**
   * Update color preview swatch
   * @param {string} hex hex
   */
  #updateColorPreview(hex: string) {
    this.colorPreview?.setAttribute(attributes.HEX, this.#findColorSwatch(hex)?.hex || hex || '');
  }

  /** Handle events */
  #detachEventHandlers(): void {
    this.offEvent('click.color-picker-container', this.container);
    this.offEvent('change.color-picer-text', this.input);
    this.offEvent('input.color-picker-text', this.input);
    this.offEvent('keydown.color-picker', this);
    this.offEvent('keydown.color-picker-popup', this.popup);
    this.#detachColorInputEventHandlers();
  }

  #attachColorInputEventHandlers(): void {
    // Respond to change events from the swatch input
    this.offEvent('change.color-picker-input', this.colorInput);
    this.onEvent('change.color-picker-input', this.colorInput, (e: any) => {
      this.#updateColor(e.target.value);
    });

    this.offEvent('input.color-picker-input', this.colorInput);
    this.onEvent('input.color-picker-input', this.colorInput, (e: any) => {
      e.stopPropagation();
      this.#updateColorPreview(e.target.value);
    });
  }

  /** Detach events */
  #detachColorInputEventHandlers(): void {
    this.offEvent('change.color-picker-input', this.colorInput);
    this.offEvent('input.color-picker-input', this.colorInput);
  }

  /**
   * Configure disabled/labels/tooltips attributes on IdsColor swatches
   * @param {boolean} shouldFocus true if a swatch should be focused
   */
  #configureSwatches(shouldFocus = false): void {
    // Use requestAnimationFrame to batch DOM operations
    requestAnimationFrame(() => {
      // Cache swatches to avoid repeated DOM queries
      const swatchesArray = this.swatches;
      const popupVisible = this.popup?.visible;
      const suppressLabels = this.suppressLabels;
      const suppressTooltips = this.suppressTooltips;
      const tooltip = this.tooltip;

      // Configure all swatches at once
      swatchesArray.forEach((swatch: IdsColor) => {
        swatch.classList.add('outlined');

        // Set tooltip based on configuration
        if (suppressTooltips) {
          swatch.tooltip = '';
          // Ensure popup content is empty when tooltips are suppressed
          if (swatch.popup) {
            swatch.popup.innerText = ' ';
          }
        } else {
          swatch.tooltip = suppressLabels ? (tooltip || swatch.hex) : (tooltip || swatch.label);
        }

        // Set tabindex based on popup visibility
        if (popupVisible) {
          // Remove tabindex attribute completely when popup is visible
          swatch.removeAttribute(attributes.TABINDEX);
        } else {
          swatch.setAttribute(attributes.TABINDEX, '-1');
        }
      });

      // Focus appropriate swatch if needed
      if (shouldFocus) {
        const selectedSwatch = this.#findColorSwatch(this.value);
        const swatchToFocus = selectedSwatch || swatchesArray[0];
        if (swatchToFocus?.swatch) {
          setTimeout(() => swatchToFocus.swatch?.focus(), 0);
        }
      }
    });
  }

  /**
   * Find a color-swatch by hex or label
   * @param {string | null} value the color's hex or label
   * @returns {IdsColor | undefined} - the matching color-swatch
   */
  #findColorSwatch(value: string | null): IdsColor | undefined {
    const colorSwatch = this.swatches.find((swatch) => {
      if (!value) {
        return !!swatch.hex === false;
      }

      const hex = String(swatch.hex).trim().toLowerCase();
      const label = String(swatch.label).trim().toLowerCase();
      return [hex, label].includes(String(value).trim().toLowerCase());
    });

    return colorSwatch;
  }

  /** @see IdsClearableMixin.appendClearableButton() */
  appendClearableButton() {
    const noColorSwatch = this.swatches.find((e: IdsColor) => !e.getAttribute(attributes.HEX));
    if (!noColorSwatch && this.initialized) {
      this.append(new IdsColor());
      this.#configureSwatches();
    }
  }

  /** @see IdsClearableMixin.removeClearableButton() */
  removeClearableButton() {
    const noColorSwatch = this.swatches.find((e: IdsColor) => !e.hasAttribute(attributes.HEX));
    noColorSwatch?.remove();
  }

  /**
   * Returns the value of the currently-selected color picker swatch
   * @returns {string | null} containing a color value
   */
  #getSelectedSwatchValue(): string | null {
    if (this.advanced) {
      return this.colorInput ? this.colorInput.value : null;
    }

    const checked = this.querySelectorAll<IdsColor>('ids-color[checked]')?.[0];
    return checked ? checked.hex : null;
  }

  /** Open/Close popup to show and hide color panel */
  #openCloseColorpicker(): void {
    if (this.advanced) {
      const label = this.container?.querySelector<HTMLLabelElement>('#label');
      if (label) {
        label.click();
      }
      return;
    }

    if (!this.popup?.visible) {
      this.open();
    } else {
      this.close();
    }
  }

  /**
   * Update color to match setected color
   * @param {string | null} value - the color's hex-value or label
   */
  #updateColor(value: string | null): void {
    if (value) {
      const colorSwatch = this.#findColorSwatch(value);
      this.value = colorSwatch?.hex || value;
    } else {
      this.value = '';
    }
  }

  /**
   * Update selected swatch color
   * @param {IdsColor} colorSwatch selected color swatch
   */
  #updateSelectedSwatch(colorSwatch?: IdsColor): void {
    const checkedColor = this.querySelector('[checked]');

    if (checkedColor) {
      checkedColor.removeAttribute(attributes.CHECKED);
    }

    if (colorSwatch) {
      colorSwatch.setAttribute(attributes.CHECKED, '');
    }
  }

  /**
   * Get the field height swatch size
   * @private
   * @returns {string} swatch size
   */
  swatchSize() {
    const fieldHeight: string = this.compact ? 'mm' : this.fieldHeight;
    const fieldSwatchSize = {
      compact: 'xs',
      xs: 'mm',
      sm: 'mm',
      mm: 'mm',
      md: 'md',
      lg: 'lg',
    }[fieldHeight] || 'md';

    return fieldSwatchSize;
  }

  /**
   * Push field-height/compact to the container element and swatch
   * @param {string} fieldHeight Field Height
   */
  onFieldHeightChange(fieldHeight: string) {
    this.input?.setAttribute(attributes.FIELD_HEIGHT, fieldHeight);
    this.colorPreview?.setAttribute(attributes.SIZE, this.swatchSize());
  }

  /**
   * Handles dirty tracker settings changes
   * @param {boolean} enabled enabled
   */
  onDirtyTrackerChange(enabled: boolean): void {
    this.input?.setAttribute(attributes.DIRTY_TRACKER, String(enabled));
  }

  /**
   * Handles label setting changes
   * @param {boolean} label label
   */
  onLabelChange(label: string) {
    this.input?.setAttribute(attributes.LABEL, label ?? '');
  }

  /**
   * Handles label state setting changes
   * @param {IdsLabelStateMode} variantName name
   */
  onLabelStateChange(variantName: IdsLabelStateMode): void {
    if (variantName) {
      this.input?.setAttribute(attributes.LABEL_STATE, variantName);
    } else {
      this.input?.removeAttribute(attributes.LABEL_STATE);
    }
  }

  /**
   * Update color picker value to match setected color hex value
   * @param {string | null} colorValue the value to update
   */
  #updateColorPickerValues(colorValue: string | null): void {
    if (!this.colorPreview) return;

    // Cache DOM elements and values to minimize property access
    const textInput = this.input;
    const colorInput = this.colorInput;
    const advanced = this.advanced;
    const suppressLabels = this.suppressLabels;

    // Get the value once
    const value = colorValue ?? this.#getSelectedSwatchValue();
    const colorSwatch = this.#findColorSwatch(value);
    const targetColorValue = colorSwatch?.hex || value;

    // Update color preview
    this.#updateColorPreview(targetColorValue ?? '');

    // Batch DOM updates
    requestAnimationFrame(() => {
      // Update advanced color input if needed
      if (advanced && colorInput) {
        colorInput.value = targetColorValue?.length !== 7 ? '#ffffff' : targetColorValue;
      }

      // Update text input value
      if (textInput) {
        if (targetColorValue) {
          const displayValue = (!suppressLabels && colorSwatch?.label) || colorSwatch?.hex || targetColorValue;
          textInput.setAttribute(attributes.VALUE, displayValue);
        } else {
          textInput.setAttribute(attributes.VALUE, '');
        }
      }
    });
  }
}
